import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMetricsUi } from "../../components/metrics/metricsUi";

// Plain-language definitions for the forecast quality metrics, shared by the
// method table and the Forecast Method cards so they always say the same thing.
// Each: a one-line meaning, how it's worked out, and how to read it.
export const METRIC_DEFINITIONS = {
  error_per_unit: {
    title: "Error per unit sold",
    text:
      "How far off the method was in testing, period by period. We hid the most recent periods, let the method forecast them, then added up how many units each period was off by (too high or too low) and divided by the units actually sold.",
    read: "20% means about 20 units off for every 100 sold. Lower is better. Under 25% is good, 25–50% is usable, above 50% is a rough guide.",
  },
  error_on_total: {
    title: "Error on total",
    text:
      "How far off the method's total for each test period was, regardless of which week or month the units fell in. Overs and unders inside the period cancel out, so it's usually lower than the error per unit sold.",
    read: "This is the one that matters most for how much to order. Lower is better.",
  },
  bias: {
    title: "Bias",
    text: "Whether the method tended to forecast too high or too low in testing, compared with what actually sold.",
    read: "+20% means it forecast 20% more than sold (risk of extra stock); −20% means 20% less (risk of running out). Closer to 0% is better.",
  },
  result: {
    title: "Result",
    text:
      "Chosen: used for this forecast. Tested: scored but not as accurate. Same as last year: the benchmark every method must beat — simply repeating past sales from the same point last time. Left out: couldn't be tested fairly, usually because the sales history is too short.",
  },
  tested_error: {
    title: "Tested error (per unit sold)",
    text:
      "The chosen method's error per unit sold in testing: we hid recent periods, let it forecast them, and compared with what actually sold.",
    read: "20% means about 20 units off for every 100 sold. Under 25% is good, 25–50% is usable, above 50% is a rough guide.",
  },
  likely_range: {
    title: "Likely range",
    text:
      "The low and high estimate for the total forecast, based on how wrong the method was in testing.",
    read: "With an 80% range, about 8 in 10 periods should land inside it. A wide range means an uncertain forecast.",
  },
  beat_benchmark: {
    title: 'Beat "same as last year" by',
    text:
      "How much smaller the chosen method's error was than simply repeating past sales from the same point last time (the same week or month last year, or the same day last week).",
    read: "Higher is better. A dash means the benchmark couldn't be tested, usually because there isn't a full year of history yet.",
  },
  demand_type: {
    title: "Demand type",
    text:
      "The pattern found in this SKU's sales — steady, growing or declining, seasonal, occasional, occasional and irregular, or new — which decides which methods are tried.",
  },
};

/**
 * The definition as tooltip content. Every colour is set here explicitly: the
 * app theme forces Typography to the page text colour (black in light mode),
 * which on MUI's default dark tooltip made the text unreadable.
 */
export const DefinitionTip = ({ def }) => {
  const ui = useMetricsUi();
  const { colors } = ui;
  return (
    <Box sx={{ maxWidth: 320, display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: ui.accent }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.3 }}>
          {def.title}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12.5, lineHeight: 1.55, color: colors.textSecondary }}>{def.text}</Typography>
      {def.read && (
        <Box
          sx={{
            bgcolor: ui.accentSoft,
            borderLeft: `3px solid ${ui.accent}`,
            borderRadius: "6px",
            px: 1.25,
            py: 0.75,
          }}
        >
          <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: colors.textPrimary }}>{def.read}</Typography>
        </Box>
      )}
    </Box>
  );
};

/** Tooltip surface styled as a small card that matches the page in both themes. */
const tooltipSlotProps = (ui) => ({
  tooltip: {
    sx: {
      bgcolor: ui.colors.bgPaper,
      color: ui.colors.textPrimary,
      border: `1px solid ${ui.colors.border}`,
      borderRadius: "10px",
      boxShadow: ui.darkMode ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(16,24,40,0.12)",
      p: "10px 12px",
      maxWidth: 344,
    },
  },
  arrow: {
    sx: {
      color: ui.colors.bgPaper,
      "&::before": { border: `1px solid ${ui.colors.border}` },
    },
  },
});

/**
 * A label with a small ⓘ that shows the metric's definition on hover (or
 * focus, for keyboard users). Falls back to the plain label when `metric`
 * has no definition.
 */
export const MetricLabel = ({ metric, children, iconSize = 13 }) => {
  const ui = useMetricsUi();
  const def = METRIC_DEFINITIONS[metric];
  if (!def) return children;
  return (
    <Tooltip
      title={<DefinitionTip def={def} />}
      arrow
      placement="top"
      enterDelay={150}
      componentsProps={tooltipSlotProps(ui)}
    >
      <Box
        component="span"
        tabIndex={0}
        aria-label={`${def.title}: ${def.text}`}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, cursor: "help", outline: "none" }}
      >
        {children}
        <InfoOutlinedIcon sx={{ fontSize: iconSize, opacity: 0.7 }} />
      </Box>
    </Tooltip>
  );
};
